import cx from 'classnames';
import type {
  FunctionComponent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useCallback } from 'react';

import type { EventContainer } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { isEmbeddedInAtlassian } from '@trello/browser';
import { dynamicToken } from '@trello/dynamic-tokens';
import { Tooltip } from '@trello/nachos/tooltip';
import { RouterLink } from '@trello/router/router-link';
import type { BoardHeaderTestIds, TestId } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { getViewMessages } from './getViewMessages';
import { getViewUrl } from './getViewUrl';
import type { SwitcherViewViewType, ViewOption } from './ViewSwitcher';

// eslint-disable-next-line @trello/less-matches-component
import * as styles from './ViewSwitcher.module.less';

interface ViewSwitcherButtonProps {
  idBoard: string;
  currentView: SwitcherViewViewType | null;
  viewType: SwitcherViewViewType;
  viewOption: ViewOption;
  analyticsContainers: EventContainer;
  isCollapsed: boolean;
}

const getViewTestId = (viewType: SwitcherViewViewType) => {
  const viewSwitcherButtonsTestIdsMap: Record<SwitcherViewViewType, TestId> = {
    Table: getTestId<BoardHeaderTestIds>('view-switcher-header-button-table'),
    Board: getTestId<BoardHeaderTestIds>('view-switcher-header-button-board'),
    Calendar: getTestId<BoardHeaderTestIds>(
      'view-switcher-header-button-calendar',
    ),
    Dashboard: getTestId<BoardHeaderTestIds>(
      'view-switcher-header-button-dashboard',
    ),
    Timeline: getTestId<BoardHeaderTestIds>(
      'view-switcher-header-button-timeline',
    ),
    Map: getTestId<BoardHeaderTestIds>('view-switcher-header-button-map'),
  };

  return viewSwitcherButtonsTestIdsMap[viewType];
};

export const ViewSwitcherButton: FunctionComponent<ViewSwitcherButtonProps> = ({
  idBoard,
  currentView,
  viewType,
  viewOption,
  analyticsContainers,
  isCollapsed,
}) => {
  const view = viewOption;
  const currentlyOnView = viewType === currentView;
  const testId = getViewTestId(viewType);

  const onViewClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (currentlyOnView: boolean, view: ViewOption) => {
      return () => {
        if (!currentlyOnView) {
          Analytics.sendClickedButtonEvent({
            buttonName: 'boardViewSwitcherButton',
            source: 'boardScreen',
            containers: analyticsContainers,
            attributes: {
              viewClicked: view.name,
            },
          });
        }
      };
    },
    [analyticsContainers],
  );

  const onKeyUp = useCallback(
    (e: ReactKeyboardEvent<Element>, callback: () => void) => {
      if (e.key === 'Enter') {
        callback();
      }
    },
    [],
  );

  const isHighlighted = !isEmbeddedInAtlassian() && currentlyOnView;

  const ViewIcon = view.icon;

  const viewMessage = getViewMessages(viewType);

  return (
    <Tooltip content={viewMessage}>
      <RouterLink
        href={getViewUrl(idBoard, view.name)}
        key={viewType}
        tabIndex={0}
        role={'link'}
        onClick={onViewClick(currentlyOnView, view)}
        aria-label={viewMessage}
        testId={testId}
        onKeyUp={(e) => onKeyUp(e, onViewClick(currentlyOnView, view))}
        className={cx(styles.viewSwitcherButton, styles.showOnNormalScreens, {
          [styles.highlighted]: isHighlighted,
          [styles.collapsed]: isCollapsed,
        })}
      >
        <span
          className={styles.viewSwitcherIcon}
          data-testid={getTestId<BoardHeaderTestIds>(
            'view-switcher-button-icon',
          )}
        >
          <ViewIcon
            size="small"
            color={
              isHighlighted
                ? dynamicToken('dynamic.button.highlighted.text')
                : dynamicToken('dynamic.text')
            }
          />
        </span>
        <span
          data-testid={getTestId<BoardHeaderTestIds>(
            'view-switcher-button-text',
          )}
          className={styles.buttonText}
        >
          {viewMessage}
        </span>
      </RouterLink>
    </Tooltip>
  );
};
